import classNames from 'classnames';
import { CopyAllButtonByExcelGenerator } from 'components/shared/CopyAllButton/CopyAllButton';
import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { LiveGreeks } from 'state/secondaryMarket/live/quote/liveQuote.model';

import { PercentOrAmountValue, Underlying } from './Cell';

export const Greeks: FC<{ data: LiveGreeks[]; greeksPrecision: number; className?: string }> = ({
  data,
  greeksPrecision,
  className,
}) => {
  const { formatMessage } = useIntl();

  const generateGridForCopyAll = useCallback(() => {
    const header = [
      formatMessage({ id: 'secondaryMarket.sidePanel.greeks.underlyingId' }),
      formatMessage({ id: 'secondaryMarket.sidePanel.greeks.spot' }),
      formatMessage({ id: 'secondaryMarket.sidePanel.greeks.delta' }),
      formatMessage({ id: 'secondaryMarket.sidePanel.greeks.gamma' }),
      formatMessage({ id: 'secondaryMarket.sidePanel.greeks.vega' }),
      formatMessage({ id: 'secondaryMarket.sidePanel.greeks.theta' }),
    ];
    const rows = data.map(d => [
      d.underlyingId ?? '_',
      d.spot?.toFixed(6) ?? '_',
      (d.deltaInPercent ?? d.deltaInAmount)?.toFixed(6) ?? '_',
      (d.gammaInPercent ?? d.gammaInAmount)?.toFixed(6) ?? '_',
      (d.vegaInPercent ?? d.vegaInAmount)?.toFixed(6) ?? '_',
      (d.thetaInPercent ?? d.thetaInAmount)?.toFixed(6) ?? '_',
    ]);
    return [header, ...rows];
  }, [data, formatMessage]);
  return (
    <>
      <table className={classNames('table table-sm table-striped', className)}>
        <thead>
          <tr>
            <th>
              <FormattedMessage id="secondaryMarket.sidePanel.greeks.underlyingId" />
            </th>
            <th>
              <FormattedMessage id="secondaryMarket.sidePanel.greeks.spot" />
            </th>
            <th>
              <FormattedMessage id="secondaryMarket.sidePanel.greeks.delta" />
            </th>
            <th>
              <FormattedMessage id="secondaryMarket.sidePanel.greeks.gamma" />
            </th>
            <th>
              <FormattedMessage id="secondaryMarket.sidePanel.greeks.vega" />
            </th>
            <th>
              <FormattedMessage id="secondaryMarket.sidePanel.greeks.theta" />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map(
            ({
              underlyingId,
              deltaInPercent,
              deltaInAmount,
              gammaInPercent,
              gammaInAmount,
              spot,
              vegaInAmount,
              vegaInPercent,
              thetaInAmount,
              thetaInPercent,
            }) => (
              <tr key={underlyingId}>
                <td>
                  <Underlying code={underlyingId} />
                </td>
                <td>{spot}</td>
                <td>
                  <PercentOrAmountValue
                    percent={deltaInPercent}
                    amount={deltaInAmount}
                    greeksPrecision={greeksPrecision}
                  />
                </td>
                <td>
                  <PercentOrAmountValue
                    percent={gammaInPercent}
                    amount={gammaInAmount}
                    greeksPrecision={greeksPrecision}
                  />
                </td>
                <td>
                  <PercentOrAmountValue
                    percent={vegaInPercent}
                    amount={vegaInAmount}
                    greeksPrecision={greeksPrecision}
                  />
                </td>
                <td>
                  <PercentOrAmountValue
                    percent={thetaInPercent}
                    amount={thetaInAmount}
                    greeksPrecision={greeksPrecision}
                  />
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      <div>
        <CopyAllButtonByExcelGenerator generateExcelGrid={generateGridForCopyAll} className="float-end" />
      </div>
    </>
  );
};

Greeks.displayName = 'Greeks';
